import React from "react";
import { errorAdd, successAdd } from "helpers/sweetalert";
import type { AlertImageUploadField, FlowEditorNode, FlowEditorState, FlowNodeKind, WidgetConfigState } from "./types";
import { uploadDonationWidgetImage } from "./upload";
import { createFlowNode, getAnchoredGridItem, getDefaultCenteredGridItem, getFlowViewport, getGridItemFromPixel, isEnabledFlowNodeKind, isNodeAnchored, resolveGridCollision, setNodeLayout } from "./flowUtils";

type ImageUploadState = { nodeId: string; field: AlertImageUploadField; progress: number; error: string } | null;

type UseFlowNodeActionsParams = {
  config: WidgetConfigState;
  selectedNodeId: string;
  onChange: React.Dispatch<React.SetStateAction<FlowEditorState>>;
  onSelectNode: (nodeId: string) => void;
  setContextMenu: React.Dispatch<React.SetStateAction<any>>;
  setImageUploadState: React.Dispatch<React.SetStateAction<ImageUploadState>>;
  setSettingsNodeId: React.Dispatch<React.SetStateAction<string>>;
};

export function useFlowNodeActions({
  config,
  selectedNodeId,
  onChange,
  onSelectNode,
  setContextMenu,
  setImageUploadState,
  setSettingsNodeId,
}: UseFlowNodeActionsParams) {
  const addNode = (kind: FlowNodeKind, position?: { x: number; y: number }) => {
    if (!isEnabledFlowNodeKind(kind)) {
      errorAdd("ຕອນນີ້ເປີດໃຊ້ສະເພາະ Donation Alert");
      return;
    }

    const node = createFlowNode({
      kind,
      position: position || { x: 0, y: 0 },
      config,
    });

    onChange((current) => ({
      ...current,
      nodes: [
        ...current.nodes,
        (() => {
          const currentViewport = getFlowViewport(current.viewport);
          const nextGridItem = position ? getGridItemFromPixel(node, currentViewport) : getDefaultCenteredGridItem(kind, currentViewport, node.data.settings);

          return setNodeLayout(node, currentViewport, resolveGridCollision(current.nodes, node.id, nextGridItem, currentViewport));
        })(),
      ],
    }));
    onSelectNode(node.id);
  };

  const updateNode = (nodeId: string, patch: Partial<FlowEditorNode>) => {
    onChange((current) => ({
      ...current,
      nodes: current.nodes.map((node) => (node.id === nodeId ? { ...node, ...patch } : node)),
    }));
  };

  const updateNodeData = (nodeId: string, patch: Partial<FlowEditorNode["data"]>) => {
    onChange((current) => ({
      ...current,
      nodes: current.nodes.map((node) =>
        node.id === nodeId
          ? (() => {
              const nextNode = {
                ...node,
                data: {
                  ...node.data,
                  ...patch,
                },
              };

              if (isNodeAnchored(nextNode) && (patch.width !== undefined || patch.height !== undefined)) {
                const currentViewport = getFlowViewport(current.viewport);
                return setNodeLayout(nextNode, currentViewport, getAnchoredGridItem(nextNode, currentViewport));
              }

              return nextNode;
            })()
          : node,
      ),
    }));
  };

  const updateNodeSetting = (nodeId: string, key: string, value: any) => {
    onChange((current) => ({
      ...current,
      nodes: current.nodes.map((node) =>
        node.id === nodeId
          ? {
              ...node,
              data: {
                ...node.data,
                settings: {
                  ...(node.data.settings || {}),
                  [key]: value,
                },
              },
            }
          : node,
      ),
    }));
  };

  const clearImageUploadState = (nodeId: string, field: AlertImageUploadField) => {
    setImageUploadState((current) => (current?.nodeId === nodeId && current.field === field ? null : current));
  };

  const uploadNodeAlertImage = async (node: FlowEditorNode, file: File, field: AlertImageUploadField) => {
    setImageUploadState({
      nodeId: node.id,
      field,
      progress: 1,
      error: "",
    });

    try {
      const uploadedUrl = await uploadDonationWidgetImage(file, (progress) => {
        setImageUploadState((current) => (current?.nodeId === node.id && current.field === field ? { ...current, progress } : current));
      });

      updateNodeSetting(node.id, field, uploadedUrl);
      updateNodeData(node.id, { imageUrl: uploadedUrl });

      if (field === "iconUrl") {
        updateNodeSetting(node.id, "iconMode", "image");
        updateNodeSetting(node.id, "showIcon", true);
        updateNodeSetting(node.id, "imageUrl", uploadedUrl);
      }

      setImageUploadState(null);
      successAdd("ອັບໂຫຼດຮູບ Alert ສຳເລັດ");
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || "ອັບໂຫຼດຮູບ Alert ບໍ່ສຳເລັດ";
      setImageUploadState({
        nodeId: node.id,
        field,
        progress: 0,
        error: message,
      });
      errorAdd(message);
    }
  };

  const deleteNode = (nodeId: string) => {
    if (!nodeId) return;
    onChange((current) => ({
      ...current,
      nodes: current.nodes.filter((node) => node.id !== nodeId),
      edges: current.edges.filter((edge) => edge?.source !== nodeId && edge?.target !== nodeId),
    }));
    if (selectedNodeId === nodeId) onSelectNode("");
    setSettingsNodeId((current) => (current === nodeId ? "" : current));
    setContextMenu(null);
  };


  return {
    addNode,
    clearImageUploadState,
    deleteNode,
    updateNode,
    updateNodeData,
    updateNodeSetting,
    uploadNodeAlertImage,
  };
}
